import { useEffect, useState } from 'react';

import type { Brand } from '../api/types';
import { useSession } from '../auth/session';

import { applyAccent } from './accent';
import { applyFavicon, applyTitle } from './browserTab';
import { useTheme } from './useTheme';

/**
 * The built-in brand, used before the resolver answers and if it cannot be
 * reached — the product still renders, just unbranded.
 */
export const DEFAULT_BRAND: Brand = {
  product_name: 'Docsight',
  chatbot_name: 'Ask Docsight',
  logo_uri: null,
  icon_uri: null,
  theme: { primary: '#1F6FEB', mode: 'system' },
};

export interface ResolvedBrand {
  brand: Brand;
  /** An object URL for the brand's logo — null while none is set, or not yet fetched. */
  logoUrl: string | null;
}

/**
 * Resolve the white-label brand for the hostname the visitor typed, apply its
 * accent/theme-default, fetch its logo, and set the browser tab's favicon and
 * title (architecture §10 `GET /v1/branding/resolve` + `/resolve/assets/{kind}`,
 * both unauthenticated calls).
 *
 * Shared by the two screens that render outside the app shell — the landing page
 * and the sign-in card — so a partner's tenants see their own name, colour, logo
 * and tab icon/title before they hold a token. Mirrors `useMyBrand`'s same four
 * effects (brand/accent, logo blob, favicon, title) — that hook resolves by the
 * signed-in caller's own tenant/partner, this one by the hostname a visitor
 * typed, since there is no session yet to resolve by.
 */
export function useBrand(): ResolvedBrand {
  const { api } = useSession();
  const { applyBrand } = useTheme();
  const [brand, setBrand] = useState<Brand>(DEFAULT_BRAND);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);

  useEffect(() => {
    const live = { current: true };
    void (async () => {
      try {
        const resolved = await api.branding.resolve(window.location.hostname);
        if (!live.current) return;
        setBrand(resolved);
        if (resolved.theme?.primary != null) applyAccent(resolved.theme.primary);
        if (resolved.theme?.mode != null) applyBrand(resolved.theme.mode);
      } catch {
        /* fall back to the built-in Docsight brand */
      }
    })();
    return () => {
      live.current = false;
    };
  }, [api, applyBrand]);

  useEffect(() => {
    applyTitle(brand.product_name);
    return () => {
      applyTitle(null);
    };
  }, [brand.product_name]);

  // A second, independent fetch, the same reason useMyBrand's is: the logo is a
  // separate (larger, optional) request, and logo_uri is a blob:// reference an
  // <img> cannot load directly, not a fetchable URL on its own.
  useEffect(() => {
    if (brand.logo_uri == null) {
      setLogoUrl(null);
      return undefined;
    }
    const controller = new AbortController();
    let objectUrl: string | null = null;
    void (async () => {
      try {
        const blob = await api.branding.downloadResolved(
          window.location.hostname,
          'logo',
          controller.signal,
        );
        if (controller.signal.aborted) return;
        objectUrl = URL.createObjectURL(blob);
        setLogoUrl(objectUrl);
      } catch {
        if (!controller.signal.aborted) setLogoUrl(null);
      }
    })();
    return () => {
      controller.abort();
      if (objectUrl !== null) URL.revokeObjectURL(objectUrl);
    };
  }, [api, brand.logo_uri]);

  useEffect(() => {
    applyFavicon(logoUrl);
    return () => {
      applyFavicon(null);
    };
  }, [logoUrl]);

  return { brand, logoUrl };
}
